/**
 * Formats a Unix timestamp as a local date string.
 *
 * Converts a Unix timestamp (in milliseconds) to a localized date string using
 * the user's system locale (en-US). The output format is: "Short Month Name Day, Year".
 *
 * @param timestamp - Unix timestamp in milliseconds representing the date to format.
 *                    Typically obtained from Date.now() or multiplying a seconds-based timestamp by 1000.
 * @returns A formatted date string in the format "Mon DD, YYYY" (e.g., "Jan 15, 2025").
 *
 * @example
 * // Returns "Jan 15, 2025"
 * formatAsLocalDate(1705276800000);
 *
 * @example
 * // Using a seconds-based timestamp
 * formatAsLocalDate(1705276800 * 1000);
 */
export function formatAsLocalDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a Unix timestamp as a local date and time string.
 *
 * Converts a Unix timestamp (in milliseconds) to a localized date and time string using
 * the user's system locale (en-US). The output format includes the date and time separated by a space,
 * with time displayed in 12-hour format with AM/PM designation.
 *
 * @param timestamp - Unix timestamp in milliseconds representing the date and time to format.
 *                    Typically obtained from Date.now() or multiplying a seconds-based timestamp by 1000.
 * @returns A formatted date-time string in the format "Mon DD, YYYY H:MM AM/PM" (e.g., "Jan 15, 2025 2:30 PM").
 *          The hour is displayed in 12-hour format without leading zeros.
 *          The minute is always displayed with 2 digits (leading zero if necessary).
 *
 * @example
 * // Returns "Jan 15, 2025 2:30 PM"
 * formatAsLocalDateTime(1705276800000);
 *
 * @example
 * // Using a seconds-based timestamp
 * formatAsLocalDateTime(1705276800 * 1000);
 *
 * @example
 * // Morning time
 * // Returns "Jan 15, 2025 9:05 AM"
 * formatAsLocalDateTime(1705310700000);
 */
export function formatAsLocalDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
